/**
 * Settings › Project tabs (design "Flow Project Tabs", board 1c): for this person, in every project. Show advanced
 * tabs (all pinned), Smart tabs (an advanced tab shows by itself when the project needs it) and each tab's place: In
 * More, Auto or Pinned (Quality starts In More). History is always one tab. Kept in this browser (utils/projectTabsModel.js).
 * Only the tabs the person's plan shows (usePlanFeatures `hiddenTabs`; Terminal also only when the plan has the
 * terminal, `terminal`): a hidden one is not listed, and its saved place is kept for when the plan shows it again.
 */
import React, { useMemo } from 'react';
import { Button, Segmented, Switch } from 'antd';
import { useTabPrefs } from '../../hooks/useProjectTabs';
import usePlanFeatures from '../../hooks/usePlanFeatures';
import config from '../../config';
import {
  BUILD_TABS, DEFAULT_TAB_PREFS, TAB_MODES, allPinned, isTabHidden, visibleAdvanced, withAllPinned, withMode,
} from '../../utils/projectTabsModel';

const always = <span className="settings-tabs-always">Always shown</span>;

export default function ProjectTabsSettings() {
  const [prefs, setPrefs] = useTabPrefs();
  const { hiddenTabs, terminal } = usePlanFeatures();
  // Logs (each service's output) exists only sandbox-first, as in the tab row (hooks/useProjectTabs.js).
  const hidden = useMemo(() => [
    ...hiddenTabs,
    ...(terminal || hiddenTabs.includes('terminal') ? [] : ['terminal']),
    ...(config.sandboxFirst || hiddenTabs.includes('logs') ? [] : ['logs']),
  ], [hiddenTabs, terminal]);
  const advanced = visibleAdvanced(hidden);
  const buildTabs = BUILD_TABS.filter((tab) => !isTabHidden(hidden, tab.id));
  const historyShown = !isTabHidden(hidden, 'history');
  const everyPinned = allPinned(prefs, hidden);
  const names = advanced.map((tab) => tab.label);
  const listed = names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names[0];
  // Reset: the defaults for the tabs shown; a hidden one keeps what was chosen for it.
  const reset = () => setPrefs((prev) => ({
    ...DEFAULT_TAB_PREFS,
    modes: Object.fromEntries(Object.keys(DEFAULT_TAB_PREFS.modes).map((id) => [id, isTabHidden(hidden, id) ? prev.modes[id] : DEFAULT_TAB_PREFS.modes[id]])),
  }));
  return (
    <section aria-labelledby="set-tabs" className="sbx-card settings-section" data-testid="settings-project-tabs">
      <h2 id="set-tabs">Project tabs</h2>
      <p className="settings-tabs-intro">For you, in every project. Other people keep their own tabs.</p>
      {advanced.length > 0 && (
        <div className="settings-row">
          <label className="settings-label" htmlFor="set-tabs-all">
            <strong>Show advanced tabs</strong>
            <span>Pins {listed} in every project. You can still unpin any of them below.</span>
          </label>
          <Switch checked={everyPinned} id="set-tabs-all" onChange={(on) => setPrefs((prev) => withAllPinned(prev, on, hidden))} />
        </div>
      )}
      {advanced.length > 0 && (
        <div className="settings-row">
          <label className="settings-label" htmlFor="set-tabs-smart">
            <strong>Smart tabs</strong>
            <span>Show an advanced tab by itself when the project needs it:</span>
            <ul className="settings-tabs-rules">
              {advanced.map((tab) => <li key={tab.id}>{tab.rule}</li>)}
            </ul>
          </label>
          <Switch checked={prefs.smart} id="set-tabs-smart" onChange={(on) => setPrefs((prev) => ({ ...prev, smart: on }))} />
        </div>
      )}
      <div className="settings-row settings-row-stack">
        <span className="settings-label">
          <strong>Your tab row</strong>
          <span>
            {historyShown ? 'Building tabs stay before the divider, History after it.' : 'Building tabs come first.'}
            {hidden.length > 0 && ' Your plan does not include every tab; the ones it leaves out are not listed.'}
            {!prefs.smart && ' Smart tabs are off: Auto tabs stay in More.'}
          </span>
        </span>
        <ul aria-label="Your tab row" className="settings-tabs-list">
          {buildTabs.map((tab) => (
            <li className="settings-tabs-item" key={tab.id}>
              <span className="settings-tabs-name"><strong>{tab.label}</strong><span>{tab.desc}</span></span>
              {always}
            </li>
          ))}
          {advanced.map((tab) => (
            <li className="settings-tabs-item" key={tab.id}>
              <span className="settings-tabs-name" id={`set-tabs-mode-${tab.id}`}><strong>{tab.label}</strong><span>{tab.short}</span></span>
              <Segmented
                aria-labelledby={`set-tabs-mode-${tab.id}`}
                data-testid={`set-tabs-mode-${tab.id}`}
                onChange={(mode) => setPrefs((prev) => withMode(prev, tab.id, mode))}
                options={TAB_MODES.map((mode) => ({ label: mode.label, value: mode.id }))}
                value={prefs.modes[tab.id]}
              />
            </li>
          ))}
          {historyShown && (
            <>
              <li aria-hidden="true" className="settings-tabs-divider"><span>Divider</span></li>
              <li className="settings-tabs-item">
                <span className="settings-tabs-name"><strong>History</strong><span>Changes, Activity, Sessions</span></span>
                {always}
              </li>
            </>
          )}
        </ul>
        <div>
          <Button onClick={reset}>Reset to default</Button>
        </div>
      </div>
    </section>
  );
}
